import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Alert,
} from "react-bootstrap";
import { Formik } from "formik";
import axios from "../../utils/axios";
import useAuth from "../../hooks/useAuth";
import { Notyf } from "notyf";

const PublicInfo = ({ user, notyf, ...props }: any) => (
  <Card>
    <Card.Header>
      <Card.Title className="mb-0">Osebne nastavitve</Card.Title>
    </Card.Header>
    <Card.Body>
      <Formik
        initialValues={{
          firstName: user?.firstName || "",
          lastName: user?.lastName || "",
          email: user?.email || "",
          mobitel: user?.mobitel || "",
          submit: false,
        }}
        validationSchema={Yup.object().shape({
          firstName: Yup.string().max(255).required("Prosim vnesi ime"),
          lastName: Yup.string().max(255).required("Prosim vnesi priimek"),
          email: Yup.string()
            .email("Prosim vnesi veljaven email")
            .max(255)
            .required("Prosim vnesi email"),
          mobitel: Yup.number().integer("Prosim vnesi samo števila"),
        })}
        onSubmit={async (
          values: any,
          { setErrors, setStatus, setSubmitting }
        ) => {
          try {
            await axios
              .put("/users/" + user.id, { user: values })
              .catch((e) => {
                notyf.error("Prišlo je do napake");
                throw e;
              });

            notyf.success("Uporabnik shranjen");
          } catch (error: any) {
            console.error(error);
            const message = error.message || "Prišlo je do napake";
            setStatus({ success: false });
            setErrors({ submit: message });
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
        }) => (
          <Form onSubmit={handleSubmit} key="profileForm">
            {errors.submit && (
              <Alert variant="danger">
                <div className="alert-message">{errors.submit}</div>
              </Alert>
            )}
            <Row>
              <Col md="4">
                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    id="firstName"
                    as="input"
                    size="lg"
                    value={values.firstName}
                    placeholder="Ime"
                    isInvalid={Boolean(touched.firstName && errors.firstName)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    aria-label="Ime"
                  />
                  {!!touched.firstName && (
                    <Form.Control.Feedback type="invalid">
                      {errors.firstName}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    id="lastName"
                    as="input"
                    size="lg"
                    value={values.lastName}
                    placeholder="Priimek"
                    isInvalid={Boolean(touched.lastName && errors.lastName)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    aria-label="Priimek"
                  />
                  {!!touched.lastName && (
                    <Form.Control.Feedback type="invalid">
                      {errors.lastName}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md="4">
                <Form.Group className="mb-3">
                  <Form.Control
                    type="email"
                    id="email"
                    as="input"
                    size="lg"
                    value={values.email}
                    placeholder="Email"
                    isInvalid={Boolean(touched.email && errors.email)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    aria-label="Email"
                  />
                  {!!touched.email && (
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    id="mobitel"
                    as="input"
                    size="lg"
                    value={values.mobitel}
                    placeholder="Telefonska"
                    isInvalid={Boolean(touched.mobitel && errors.mobitel)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    aria-label="Telefonska"
                  />
                  {!!touched.mobitel && (
                    <Form.Control.Feedback type="invalid">
                      {errors.mobitel}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Button variant="primary" type="submit">
              Shrani
            </Button>
          </Form>
        )}
      </Formik>
    </Card.Body>
  </Card>
);

const Profile = () => {
  const { user } = useAuth();
  const [currentUser, setCurrentUser] = useState(user);

  useEffect(() => {
    if (!user && currentUser) return;
    setCurrentUser(user);
  }, [user, currentUser]);

  const notyf = new Notyf({
    duration: 5000,
    position: {
      x: "right",
      y: "bottom",
    },
  });

  return (
    <React.Fragment>
      <Helmet title="Profil" />
      <Container fluid className="p-0">
        <h1 className="h3 mb-3">Nastavitve računa</h1>

        <Row>
          <Col>
            {currentUser ? (
              <PublicInfo notyf={notyf} user={currentUser} />
            ) : (
              <div />
            )}
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default Profile;
